/**
 * /tags/categories[/:id] — the tag categories and their schema, read-only (metaplan A10; AIE §10 "Read-only
 * views … tag categories (with schema)"): what attributes a tag of each category carries, which of them are
 * multilingual, which are required. Opened from the Tags list toolbar ("Categories").
 *
 * The list is the existing `/v2/edit/tour_tag_category/` viewset (licensee and parent categories); the
 * detail reads the category's EFFECTIVE schema — its inline `schema`, else its shared `schema_ref`'s — from
 * `GET /tour_tag_category/{id}/effective_schema/` (contract ../apiv2/dashboard_ai_edit.openapi.yaml).
 */
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';

import { DataAPIContext } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import PageMeta from '../components/PageMeta';
import { getEffectiveSchema, listTagCategories } from '../notifications/aiChanges/api';
import { CodeView } from '../notifications/content/common';
import { ContextStore } from '../store/contextStore';

const BASE = '/tags/categories';

const typeOf = (field) => {
    if (!field || typeof field !== 'object') return '—';
    const t = Array.isArray(field.type) ? field.type.join(' | ') : field.type;
    return [t, field.format].filter(Boolean).join(' · ') || (field.enum ? 'enum' : '—');
};

/** Properties of a JSON schema in display order (`x-displayOrder` first, then by key). */
export const schemaRows = (schema) => {
    const props = (schema && schema.properties) || {};
    const required = new Set(Array.isArray(schema?.required) ? schema.required : []);
    return Object.keys(props)
        .filter((k) => props[k] && typeof props[k] === 'object')
        .sort((a, b) => {
            const oa = props[a]['x-displayOrder'];
            const ob = props[b]['x-displayOrder'];
            if (oa !== undefined && ob !== undefined) return oa - ob;
            if (oa !== undefined) return -1;
            if (ob !== undefined) return 1;
            return a.localeCompare(b);
        })
        .map((key) => ({
            key,
            title: props[key].title || props[key].name || '',
            type: typeOf(props[key]),
            multilingual: !!(props[key]['x-multilingual'] || props[key].multilingual),
            required: required.has(key),
            description: props[key].description || '',
        }));
};

const CategoryDetail = ({ id }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [state, setState] = React.useState({ loading: true, data: null, error: null });
    React.useEffect(() => {
        let alive = true;
        getEffectiveSchema(dataAPI, id)
            .then((data) => alive && setState({ loading: false, data, error: null }))
            .catch((error) => alive && setState({ loading: false, data: null, error }));
        return () => {
            alive = false;
        };
    }, [dataAPI, id]);
    if (state.loading) return <CircularProgress size={24} />;
    if (state.error) {
        return <Alert severity={state.error.status === 404 ? 'warning' : 'error'}>{state.error.status === 404 ? 'Not found (or not visible to you).' : state.error.message}</Alert>;
    }
    const c = state.data;
    const rows = schemaRows(c.schema);
    return (
        <Box>
            <PageMeta title={`Tag category - ${c.name}`} />
            <Typography variant="overline" color="text.secondary">Tag category</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6">{c.name}</Typography>
                <Chip size="small" variant="outlined" label={c.code} sx={{ fontFamily: 'monospace' }} />
                {c.source === 'ref' && c.schema_ref && <Chip size="small" color="info" variant="outlined" label={`Shared schema: ${c.schema_ref.name}`} />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The attributes a tag of this category carries. Read-only here.
            </Typography>
            {c.source === 'none' || rows.length === 0 ? (
                <Typography variant="body2">This category defines no attributes.</Typography>
            ) : (
                <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 2 }}>
                    <Table size="small" aria-label="Schema attributes">
                        <TableHead>
                            <TableRow>
                                <TableCell>Attribute</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Languages</TableCell>
                                <TableCell>Required</TableCell>
                                <TableCell>Description</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.key}>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.key}</TableCell>
                                    <TableCell>{r.title || '—'}</TableCell>
                                    <TableCell>{r.type}</TableCell>
                                    <TableCell>{r.multilingual ? 'Per language' : 'One value'}</TableCell>
                                    <TableCell>{r.required ? 'Yes' : ''}</TableCell>
                                    <TableCell>{r.description}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
            {c.schema && <CodeView label="JSON schema" value={c.schema} />}
        </Box>
    );
};

const CategoryList = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const [query, setQuery] = React.useState('');
    const [state, setState] = React.useState({ loading: true, rows: [], error: null });
    React.useEffect(() => {
        let alive = true;
        listTagCategories(dataAPI)
            .then((data) => alive && setState({ loading: false, rows: data?.results || [], error: null }))
            .catch((error) => alive && setState({ loading: false, rows: [], error }));
        return () => {
            alive = false;
        };
    }, [dataAPI]);
    const q = query.trim().toLowerCase();
    const rows = q ? state.rows.filter((r) => `${r.name} ${r.code}`.toLowerCase().includes(q)) : state.rows;
    return (
        <Box>
            <PageMeta title="Tag categories" />
            <Typography variant="h6">Tag categories</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What kinds of tags exist and the attributes each carries. Read-only.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <TextField size="small" label="Search" value={query} onChange={(ev) => setQuery(ev.target.value)} />
                {state.loading && <CircularProgress size={20} />}
            </Stack>
            {state.error && <Alert severity="error">Could not load the categories: {state.error.message}</Alert>}
            {rows.length > 0 && (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="Tag categories">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Owner</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => history.push(`${BASE}/${r.id}`)}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.code}</TableCell>
                                    <TableCell>
                                        {r.tour_operator?.name || '—'}
                                        {r.is_inherited ? <Chip size="small" variant="outlined" label="Inherited" sx={{ ml: 1 }} /> : null}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            {r.external_identity && <Chip size="small" variant="outlined" color="info" label="Externally identified" />}
                                            {r.state_category && <Chip size="small" variant="outlined" label="State" />}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
};

const TagCategories = () => {
    const { id } = useParams();
    const { hasPerm } = React.useContext(ContextStore);
    if (!(typeof hasPerm === 'function' && hasPerm('tag', 'list'))) return <AccessDeniedView />;
    return (
        <Box p={2}>
            <Typography variant="body2" sx={{ mb: 1 }}>
                <RouterLink to={id ? BASE : '/tags'}>← {id ? 'Tag categories' : 'Tags'}</RouterLink>
            </Typography>
            {id ? <CategoryDetail id={id} /> : <CategoryList />}
        </Box>
    );
};

export default TagCategories;
